import React from 'react';
import { Briefcase, FileText, AlertCircle, Calendar, TrendingUp, User, Coins } from 'lucide-react';
import StatusBadge from '../../../components/status/StatusBadge';
import TransactionProgress from './TransactionProgress';
import { TRANSACTION_TYPES_LABELS } from '../../../constants/transactionTypes';

export default function TransactionHeader({ transaction }) {
  if (!transaction) return null;

  // Mock alerts array (would come from transaction data in real life)
  const alerts = []; 
  // const alerts = ["Documents KYC manquants pour les intervenants."];

  return (
    <section className="relative overflow-hidden rounded-2xl border border-workspace bg-surface shadow-hp-md transition-all duration-300">
      {/* Arrière-plan Subpart avec Overlay Gradient */}
      <div 
        aria-hidden="true" 
        className="pointer-events-none absolute inset-0 z-0 bg-cover bg-right bg-no-repeat opacity-15 dark:opacity-20 mix-blend-overlay"
        style={{ backgroundImage: "url('/assets/subparts/subpart_1.png')" }}
      />
      <div 
        aria-hidden="true" 
        className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-surface via-surface/95 to-surface/80"
      />

      <div className="relative z-10 p-5 sm:p-6">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
          
          {/* Partie Gauche : Titre et Informations */}
          <div className="min-w-0 xl:flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-surface-muted border border-workspace px-3 py-1 text-xs font-mono font-semibold text-secondary shadow-hp-sm">
                <Briefcase className="h-3.5 w-3.5 text-secondary" />
                #{transaction.reference}
              </span>

              <StatusBadge status={transaction.status} type="transaction" />

              <span className="inline-flex items-center rounded-lg bg-secondary-50 dark:bg-secondary-900/40 px-3 py-1 text-xs font-semibold text-secondary border border-secondary-100 dark:border-secondary-800">
                {TRANSACTION_TYPES_LABELS[transaction.transactionType] || transaction.transactionType}
              </span>

              {transaction.originDossier && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-lg bg-surface-muted/90 px-3 py-1 text-xs font-medium text-subtle border border-workspace shadow-hp-sm"
                  title="Dossier d'analyse initial du Comité Forum (converti)"
                >
                  <FileText className="h-3.5 w-3.5 text-tertiary" />
                  Dossier Forum d'origine : #{transaction.originDossier.reference}
                </span>
              )}
            </div>

            <h1 className="mt-3 truncate text-2xl font-bold tracking-tight text-primary sm:text-3xl">
              {transaction.title}
            </h1>

            {/* Pillules de Métadonnées Clés */}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
              <div className="inline-flex items-center gap-2 rounded-xl bg-surface-elevated border border-workspace px-3 py-1.5 shadow-hp-xs">
                <Coins className="h-4 w-4 text-tertiary" />
                <span className="text-subtle">Devise :</span>
                <span className="font-bold text-primary">{transaction.currency || "EUR"}</span>
              </div>

              <div className="inline-flex items-center gap-2 rounded-xl bg-surface-elevated border border-workspace px-3 py-1.5 shadow-hp-xs">
                <Calendar className="h-4 w-4 text-tertiary" />
                <span className="text-subtle">Durée :</span>
                <span className="font-bold text-primary">
                  {transaction.durationMonths ? `${transaction.durationMonths} mois` : "Non spécifiée"}
                </span>
              </div>

              {transaction.yieldRate != null && parseFloat(transaction.yieldRate) > 0 && (
                <div className="inline-flex items-center gap-2 rounded-xl bg-surface-elevated border border-workspace px-3 py-1.5 shadow-hp-xs">
                  <TrendingUp className="h-4 w-4 text-[var(--hp-success-600)]" />
                  <span className="text-subtle">Rendement :</span>
                  <span className="font-bold text-[var(--hp-success-700)]">{transaction.yieldRate}%</span>
                </div>
              )}

              <div className="inline-flex items-center gap-2 rounded-xl bg-surface-elevated border border-workspace px-3 py-1.5 shadow-hp-xs">
                <User className="h-4 w-4 text-tertiary" />
                <span className="text-subtle">Responsable :</span>
                <span className="font-bold text-primary">
                  {transaction.manager?.displayName || transaction.manager?.Nom || "Non attribué"}
                </span>
              </div>
            </div>
          </div>

          {/* Partie Droite : Alertes et Frise Chronologique */}
          <div className="w-full shrink-0 xl:w-auto xl:max-w-2xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:gap-6">
              
              {/* Colonne 1 : Alertes */}
              {alerts && alerts.length > 0 && (
                <div className="flex w-full flex-col sm:w-64 shrink-0 sm:justify-center">
                  <div className="rounded-xl border border-[var(--hp-danger-200)] bg-[var(--hp-danger-50)] p-3 shadow-hp-xs dark:border-[var(--hp-danger-800)] dark:bg-[var(--hp-danger-900)]">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hp-danger-600)] dark:text-[var(--hp-danger-400)]" />
                      <ul className="space-y-1">
                        {alerts.map((alert, idx) => (
                          <li key={idx} className="text-xs font-semibold text-[var(--hp-danger-700)] dark:text-[var(--hp-danger-300)]">
                            {alert}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Séparateur Vertical conditionnel */}
              {alerts && alerts.length > 0 && (
                <div className="hidden sm:block w-px self-stretch bg-workspace" />
              )}
              
              {/* Colonne 2 : Frise de Progression */}
              <div className="flex flex-1 items-center sm:min-w-[320px]">
                <TransactionProgress status={transaction.status} compact={true} />
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
